import Udesk from 'Udesk';
import React, { FC, useMemo } from 'react';
import { Tooltip } from 'udesk-ui';
import { InformationOutlined } from '@udesk/icons';
import UdeskLocales from 'UdeskLocales';

interface TemplateProps {
    type?: number;
}

const Template: FC<TemplateProps> = (props) => {
    const { type } = props;

    const title = useMemo(() => {
        switch (type) {
            case Udesk.enums.operatorTypes.keywordStrict.id:
                return /* 关键词使用严格匹配，一行支持输入多个关键词，关键词间使用英文逗号分隔。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .keywordsUseStrictMatchingOneLineCanEnterMultipleKeywordsKeywordsAreSeparatedByCommas;

            case Udesk.enums.operatorTypes.semanticTags.id:
                return /* 语义标签同时支持严格匹配和语义匹配，其中语义标签需在智能工具侧边栏或语义智能-语义标签中进行配置。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .semanticTagsSupportBothStrictMatchingAndSemanticMatchingWhereSemanticTagsNeedToBeConfiguredInTheSidebarOfSmartToolsOrSemanticIntelligencesemanticTags;

            case Udesk.enums.operatorTypes.conceptWord.id:
                return /* 概念词是专为词语设计的”语义标签”，可对词语进行严格匹配或语义匹配，其中概念词在语义智能-概念词中进行配置。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .conceptWordsAresemanticLabelsDesignedForWordsWhichCanMatchWordsStrictlyOrSemanticallyWhereConceptWordsAreConfiguredInSemanticIntelligenceconceptWords;

            case Udesk.enums.operatorTypes.agentAnswer.id:
                return /* 检测客服是否应答超时、客户是否应答超时、客户是否无响应。其中应答起始句指，当角色A连续发送消息，检测角色B的应答情况时，是从连续发送的多条消息的首句还是末句进行检测。响应超时是升级版的静默检测。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .checkWhetherTheCustomerServiceResponseTimedOutWhetherTheCustomerRespondedTimedOutAndWhetherTheCustomerDidNotRespondTheResponseStartSentenceRefersToWhetherTheResponseOfRoleAIsDetectedFromTheFirstSentenceOrTheLastSentenceOfMultipleMessagesSentContinuouslyWhenRoleAContinuouslySendsMessagesAndDetectsTheResponseOfRoleBResponseTimeoutIsAnUpgradedVersionOfSilentDetection;

            case Udesk.enums.operatorTypes.dialogue.id:
                return /* 检测各角色质检的静默时长，默认从末句开始检测。 */ UdeskLocales['current']
                    .components.checkPointConditionListGather.checkPointCondition.components.info
                    .index
                    .theSilenceDurationOfEachCharacterIsDetectedByDefaultTheLastSentenceIsDetected;

            case Udesk.enums.operatorTypes.grabDetection.id:
                return /* 两个不同角色的对话，上一句的结束时间 - 当前句的开始时间 >= 抢话间隔；且当前句的结束时间 - 当前句的开始时间 > 抢话句长；同时满足则判定为抢话。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .inTheDialogueBetweenTwoDifferentRolesTheEndTimeOfThePreviousSentencetheStartTimeOfTheCurrentSentenceTheSnatchingIntervalAndTheEndTimeOfTheCurrentSentencetheStartTimeOfTheCurrentSentenceTheLengthOfTheSnatchingSentenceIfItIsSatisfiedAtTheSameTimeItWillBeJudgedAsSnatchingWords;

            case Udesk.enums.operatorTypes.questionsAndAnswers.id:
                return /* 适用于一问一答类型的业务知识解答检测。该算子优先从客户的消息中检测问题。若未检测出问题，则遵循配置项”未检测出问题时”相关配置。若检测出问题，则从客服的消息中检测答案。当检测到答案，则判定为客服知识解答正确；未检测到答案，则判定为客服知识解答错误。该算子为旧版知识解答，对应选项在语义智能-交互识别中进行配置。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .itIsSuitableForBusinessKnowledgeAnswerDetectionOfOneQuestionAndOneAnswerTypeTheOperatorPreferentiallyDetectsProblemsFromTheCustomersMessagesIfTheProblemIsNotDetectedFollowTheConfigurationOfTheConfigurationItemWhenTheProblemIsNotDetectedIfAProblemIsDetectedTheAnswerIsDetectedFromTheCustomerServiceMessageWhenAnAnswerIsDetectedItIsDeterminedThatTheCustomerServiceKnowledgeSolutionIsCorrectIfNoAnswerIsDetectedItIsDeterminedThatTheCustomerServiceKnowledgeSolutionIsWrongThisOperatorIsTheOldVersionOfTheKnowledgeSolutionAndTheCorrespondingOptionsAreConfiguredInSemanticIntelligenceInteractiveRecognition;

            case Udesk.enums.operatorTypes.repeat.id:
                return /* 检测会话中是否存在重复发送的消息，既支持严格意义上的重复，也支持语义上的重复。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .detectingWhetherThereAreDuplicateMessagesInASessionSupportsBothStrictAndSemanticDuplication;

            case Udesk.enums.operatorTypes.timeInterval.id:
                return /* 支持检测两个语义间的间隔时长，其中语义标签支持在智能工具侧边栏或语义智能-语义标签进行设置。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .itSupportsTheDetectionOfTheIntervalBetweenTwoSemanticsInWhichSemanticLabelsCanBeSetInTheSidebarOfIntelligentToolsOrSemanticIntelligencesemanticLabels;

            case Udesk.enums.operatorTypes.knowledge.id:
                return /* 适用于一问一答类型的业务知识解答检测。该算子优先从客户的消息中检测问题。若未检测出问题，则遵循配置项”未检测出问题时”相关配置。若检测出问题，则从客服的消息中检测答案。当检测到答案，则判定为客服知识解答正确；未检测到答案，则判定为客服知识解答错误。该算子为新版知识解答，对应选项在语义智能-知识库中进行配置。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .itIsSuitableForBusinessKnowledgeAnswerDetectionOfOneQuestionAndOneAnswerTypeTheOperatorPreferentiallyDetectsProblemsFromTheCustomersMessagesIfTheProblemIsNotDetectedFollowTheConfigurationOfTheConfigurationItemWhenTheProblemIsNotDetectedIfAProblemIsDetectedTheAnswerIsDetectedFromTheCustomerServiceMessageWhenAnAnswerIsDetectedItIsDeterminedThatTheCustomerServiceKnowledgeSolutionIsCorrectIfNoAnswerIsDetectedItIsDeterminedThatTheCustomerServiceKnowledgeSolutionIsWrongThisOperatorIsANewVersionOfTheKnowledgeSolutionAndTheCorrespondingOptionsAreConfiguredInTheSemanticIntelligenceknowledgeBase;

            case Udesk.enums.operatorTypes.sentenceLength.id:
                return /* 检测客服句长是否过短或过长。 */ UdeskLocales['current'].components
                    .checkPointConditionListGather.checkPointCondition.components.info.index
                    .checkWhetherTheCustomerServiceSentenceIsTooShortOrTooLong;

            case Udesk.enums.operatorTypes.emotion.id:
                return /* 检测客服或客户的情绪。 */ UdeskLocales['current'].components
                    .checkPointConditionListGather.checkPointCondition.components.info.index
                    .detectTheEmotionsOfAgentsOrCustomers;

            case Udesk.enums.operatorTypes.typoDetection.id:
                return /* 仅适用于文本对话质检，检测发送的消息中是否包含错别字。可在语义智能-智能词库-错别字词集中丰富错别字词，从而优化错别字检测效果。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .onlyApplicableToTextConversationQualityInspectionToDetectWhetherTheSentMessageContainsTyposTheTyposCanBeEnrichedInTheSemanticIntelligenceintelligentThesaurustyposToOptimizeTheDetectionOfTypos;

            case Udesk.enums.operatorTypes.question.id:
                return /* 仅适用于语音通话质检，检测会话中是否出现疑问句。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .itIsOnlyApplicableToVoiceCallQualityInspectionWhichDetectsWhetherInterrogativeSentencesAppearInTheSession;

            case Udesk.enums.operatorTypes.volume.id:
                return /* 仅适用于语音通话质检，检测客服或客户的音量是否过大或过小、以及音量是否突然变化。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .itIsOnlyApplicableToVoiceCallQualityInspectionWhichDetectsWhetherTheVolumeOfTheCustomerServiceOrCustomerIsTooHighOrTooLowAndWhetherTheVolumeChangesSuddenly;

            case Udesk.enums.operatorTypes.speed.id:
                return /* 仅适用于语音通话质检，检测客服或客户的语速。 */ UdeskLocales['current']
                    .components.checkPointConditionListGather.checkPointCondition.components.info
                    .index
                    .itIsOnlyApplicableToVoiceCallQualityInspectionAndDetectsTheSpeechSpeedOfCustomerServiceOrCustomers;

            case Udesk.enums.operatorTypes.entityInfoDetection.id:
                return /* 检测会话中提及的信息实体是否正确，即判断信息实体值是否等于某个随路数据或固定值。其中信息实体可以在语义智能-信息实体中进行配置。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .checkWhetherTheInformationEntityMentionedInTheSessionIsCorrectThatIsJudgeWhetherTheInformationEntityValueIsEqualToACertainPathassociatedDataOrFixedValueTheInformationEntityCanBeConfiguredInTheSemanticIntelligenceinformationEntity;

            case Udesk.enums.operatorTypes.associate.id:
                return /* 新版随路字段：检测随路数据是否等于固定值。旧版随路数据：可检测随路数据是否满足关系表达式，其中关系表达式支持用户自定义，可设置加减乘除运算、是否匹配语义标签、是否包含关键词、IF判断等。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .theNewVersionOfTheAccompanyingFieldDetectsWhetherTheAccompanyingDataIsEqualToAFixedValueOldVersionOfDataWithTheRouteCanDetectWhetherTheDataWithTheRouteMeetsTheRelationshipExpressionWhereTheRelationshipExpressionSupportsUserdefinedYouCanSetTheAdditionSubtractionMultiplicationAndDivisionOperationWhetherToMatchTheSemanticLabelWhetherToContainKeywordsIFJudgmentEtc;

            case Udesk.enums.operatorTypes.businessSummary.id:
                return /* 判断会话的小结内容是否等于或包含固定值。其中小结在流程自动化-业务小结中进行设置。 */ UdeskLocales[
                    'current'
                ].components.checkPointConditionListGather.checkPointCondition.components.info.index
                    .determineWhetherTheSummaryContentOfTheSessionIsEqualToOrContainsAFixedValueTheSummaryIsSetInProcessAutomationBusinessSummary;

            case Udesk.enums.operatorTypes.semanticMatching.id:
                return /* 利用大模型进行语义匹配。 */UdeskLocales['current'].components.checkPointConditionListGather.checkPointCondition.components.info.index.useLargeModelsForSemanticMatching;

            default:
                return '';
        }
    }, [type]);

    return (
        <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{title}</div>}>
            {title ? <InformationOutlined /> : null}
        </Tooltip>
    );
};

export default Template;
